import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Button,
  Checkbox,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";

const PHONE_REGEX = /^[6-9]\d{9}$/;
const OTP_RESEND_SECONDS = 30;

const DeclarationCheckBox = () => {
  const [checked, setChecked] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [examId, setExamId] = useState(null);

  // Name & Phone
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");

  // OTP popup
  const [otpOpen, setOtpOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);

  // Verify & Submit state
  const [isVerified, setIsVerified] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Resend timer
  const [resendTimer, setResendTimer] = useState(0);
  const timerRef = useRef(null);

  // get examId from sessionStorage
  useEffect(() => {
    const id = sessionStorage.getItem("examId");
    setExamId(id);
  }, []);

  // Countdown effect
  useEffect(() => {
    if (resendTimer > 0) {
      timerRef.current = setTimeout(() => setResendTimer((t) => t - 1), 1000);
    }
    return () => clearTimeout(timerRef.current);
  }, [resendTimer]);

  // ── helpers ──────────────────────────────────────────
  const isPhoneValid = PHONE_REGEX.test(phone);
  const isFormValid = checked && name.trim().length > 0 && isPhoneValid;

  const startResendTimer = useCallback(() => setResendTimer(OTP_RESEND_SECONDS), []);

  // ── Send OTP ─────────────────────────────────────────
  const sendOtp = async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/v1/parikshado/student/exam/auth/otp_send`,
        { user: { phone } }
      );
      if (!res?.data?.success) {
        alert("Failed to send OTP");
        return false;
      }
      return true;
    } catch (err) {
      console.error("Send OTP error:", err);
      alert("Something went wrong while sending OTP");
      return false;
    }
  };

  // ── Main Submit → open OTP popup ─────────────────────
  const handleSubmit = async () => {
    if (!isPhoneValid) {
      setPhoneError("Enter a valid 10-digit phone number");
      return;
    }
    const sent = await sendOtp();
    if (sent) {
      setOtp("");
      setOtpError("");
      setIsVerified(false);
      setOtpOpen(true);
      startResendTimer();
    }
  };

  // ── Verify OTP ────────────────────────────────────────
  const handleVerifyOtp = async () => {
    if (otp.length < 4) {
      setOtpError("Enter a valid OTP");
      return;
    }
    setOtpLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/v1/parikshado/student/exam/auth/otp_verify`,
        { user: { phone, otp } }
      );
      if (response?.data?.success) {
        setIsVerified(true); // green button, Submit appears
        setOtpError("");
      } else {
        setOtpError("Invalid OTP. Please try again.");
      }
    } catch (error) {
      console.error("Verify OTP error:", error);
      setOtpError(error?.response?.data?.message || "Something went wrong while verifying OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  // ── Final Submit → details_add API + paper_check status PUT ──────────
  const handleFinalSubmit = async () => {
    setSubmitLoading(true);
    try {
      const [detailsResponse, statusResponse] = await Promise.all([
        axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/paper_check/status/details_add`,
          {
            details: {
              exam_id: String(examId),
              phone: phone,
              full_name: name.trim(),
            },
          }
        ),
        axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/paper_check/status/${examId}`
        ),
      ]);

      if (detailsResponse?.data?.success && statusResponse?.data?.success) {
        setSubmitted(true);
        setOtpOpen(false);
        window.close();
      } else {
        alert("Submission failed. Please try again.");
      }
    } catch (error) {
      console.error("Final submit error:", error);
      alert(error?.response?.data?.message || "Something went wrong while submitting");
    } finally {
      setSubmitLoading(false);
    }
  };

  // ── Resend OTP → reset otp + verify state ─────────────
  const handleResendOtp = async () => {
    setOtpError("");
    setOtp("");           // OTP field empty
    setIsVerified(false); // Verify button reset
    const sent = await sendOtp();
    if (sent) {
      startResendTimer();
    }
  };

  // ── Phone validation ──────────────────────────────────
  const handlePhoneChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
    setPhone(val);
    if (val.length === 10 && !PHONE_REGEX.test(val)) {
      setPhoneError("Phone number must start with 6-9");
    } else {
      setPhoneError("");
    }
  };

  return (
    <>
      <Box
        sx={{
          mt: 3,
          mb: 2,
          p: 2,
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          backgroundColor: "#fafafa",
        }}
      >
        {/* Name & Phone fields */}
        <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
          <TextField
            label="Name *"
            variant="outlined"
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={submitted}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <TextField
            label="Phone Number *"
            variant="outlined"
            size="small"
            value={phone}
            onChange={handlePhoneChange}
            disabled={submitted}
            error={!!phoneError}
            helperText={phoneError}
            inputProps={{ maxLength: 10, inputMode: "numeric" }}
            sx={{ flex: 1, minWidth: 200 }}
          />
        </Box>

        {/* Declaration checkbox */}
        <Box display="flex" alignItems="center" gap={1}>
          <Checkbox
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            disabled={submitted}
          />
          <Typography variant="body2">
            I hereby declare that I have checked and verified all the questions
            and answers in this examination paper, and they are correct. I also
            confirm that the question sections and subject-wise distribution of
            the paper are accurate as per the prescribed structure.
          </Typography>
        </Box>

        {/* Submit button */}
        <Box sx={{ mt: 2, textAlign: "right" }}>
          <Button
            variant="contained"
            color="primary"
            disabled={!isFormValid || submitted}
            onClick={handleSubmit}
          >
            {submitted ? "Submitted" : "Submit"}
          </Button>
        </Box>
      </Box>

      {/* ══════ OTP Dialog ══════ */}
      <Dialog open={otpOpen} onClose={() => setOtpOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Verify OTP
          <IconButton
            aria-label="close"
            onClick={() => setOtpOpen(false)}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            An OTP has been sent to <strong>{phone}</strong>
          </Typography>

          {/* OTP field + inline Verify button */}
          <Box display="flex" gap={1} alignItems="flex-start">
            <TextField
              label="Enter OTP"
              variant="outlined"
              size="small"
              fullWidth
              value={otp}
              onChange={(e) => {
                setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                setOtpError("");
              }}
              error={!!otpError}
              helperText={otpError}
              inputProps={{ maxLength: 6, inputMode: "numeric" }}
              disabled={isVerified}
              InputProps={
                isVerified
                  ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <CheckCircleIcon sx={{ color: "green" }} />
                      </InputAdornment>
                    ),
                  }
                  : undefined
              }
            />
            {/* Verify Button — green & disabled after success */}
            <Button
              variant="contained"
              size="small"
              onClick={handleVerifyOtp}
              disabled={otp.length < 4 || otpLoading || isVerified}
              sx={{
                mt: 0.2,
                minWidth: 90,
                height: 40,
                flexShrink: 0,
                backgroundColor: isVerified ? "green" : undefined,
                "&:hover": {
                  backgroundColor: isVerified ? "green" : undefined,
                },
                "&.Mui-disabled": {
                  backgroundColor: isVerified ? "green !important" : undefined,
                  color: isVerified ? "#fff !important" : undefined,
                },
              }}
            >
              {otpLoading ? "..." : isVerified ? "Verified ✓" : "Verify"}
            </Button>
          </Box>

          {/* Resend row */}
          <Box display="flex" alignItems="center" mt={2}>
            {resendTimer > 0 ? (
              <Typography variant="caption" color="text.secondary">
                Resend OTP in {resendTimer}s
              </Typography>
            ) : (
              <Button
                size="small"
                onClick={handleResendOtp}
                disabled={isVerified}
              >
                Resend OTP
              </Button>
            )}
          </Box>

        </DialogContent>

        {/* Submit — always visible, disabled until OTP verified */}
        <DialogActions sx={{ px: 3, pb: 2, textAlign: "right" }}>
          <Button
            variant="contained"
            color="success"
            fullWidth
            onClick={handleFinalSubmit}
            disabled={!isVerified || submitLoading}
          >
            {submitLoading ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeclarationCheckBox;
